import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  CircularProgress,
  FormControlLabel,
  Switch,
  Autocomplete,
  Chip,
  LinearProgress,
  Alert,
  AlertTitle,
  Paper,
} from "@mui/material";
import {
  Psychology as PsychologyIcon,
  Analytics as AnalyticsIcon,
  Storage as StorageIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { ptBR } from 'date-fns/locale';
import { format, subDays } from 'date-fns';

import { i18n } from "../../../translate/i18n";
import api from "../../../services/api";
import toastUtils from "../../../utils/toastUtils";

const TrainingModal = ({ open, onClose, assistant, onTrainingComplete }) => {
  const [loading, setLoading] = useState(false);
  const [queues, setQueues] = useState([]);
  const [loadingQueues, setLoadingQueues] = useState(false);

  // Estados para configuração do treinamento
  const [config, setConfig] = useState({
    maxTickets: 500,
    dateRange: {
      start: subDays(new Date(), 90), // Últimos 90 dias por padrão
      end: new Date()
    },
    queueIds: [],
    includeClosedTickets: true,
    minMessagesPerTicket: 3,
    onlySuccessfulTickets: false
  });

  // Estados para resultado do treinamento
  const [trainingResult, setTrainingResult] = useState(null);
  const [trainingProgress, setTrainingProgress] = useState(0);

  useEffect(() => {
    if (open) {
      fetchQueues();
      resetModal();
    }
  }, [open]);

  const resetModal = () => {
    setTrainingResult(null);
    setTrainingProgress(0);
    setConfig({
      maxTickets: 500,
      dateRange: {
        start: subDays(new Date(), 90),
        end: new Date()
      },
      queueIds: [],
      includeClosedTickets: true,
      minMessagesPerTicket: 3,
      onlySuccessfulTickets: false
    });
  };

  const fetchQueues = async () => {
    setLoadingQueues(true);
    try {
      const { data } = await api.get("/queue");
      setQueues(data);
    } catch (err) {
      console.error("Erro ao carregar filas:", err);
      toastUtils.error("Erro", "Não foi possível carregar as filas");
    } finally {
      setLoadingQueues(false);
    }
  };

  const handleStartTraining = async () => {
    setLoading(true);
    setTrainingProgress(10);

    try {
      // Simular progresso
      const progressInterval = setInterval(() => {
        setTrainingProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 1000);

      const { data } = await api.post(`/assistants/${assistant.id}/train-with-tickets`, {
        maxTickets: config.maxTickets,
        dateRange: {
          start: format(config.dateRange.start, 'yyyy-MM-dd'),
          end: format(config.dateRange.end, 'yyyy-MM-dd')
        },
        queueIds: config.queueIds.length > 0 ? config.queueIds : undefined,
        includeClosedTickets: config.includeClosedTickets,
        minMessagesPerTicket: config.minMessagesPerTicket,
        onlySuccessfulTickets: config.onlySuccessfulTickets
      });

      clearInterval(progressInterval);
      setTrainingProgress(100);
      setTrainingResult(data.data);

      toastUtils.success(
        "Treinamento Concluído",
        `Assistente ${assistant.name} foi treinado com sucesso com ${data.data.ticketsProcessed} tickets`
      );

      if (onTrainingComplete) {
        onTrainingComplete(data.data);
      }

    } catch (err) {
      console.error("Erro no treinamento:", err);
      setTrainingProgress(0);
      toastUtils.error(
        "Erro no Treinamento",
        err.response?.data?.error || "Não foi possível completar o treinamento"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setTimeout(resetModal, 300); // Delay para animação do modal
    }
  };

  const renderProgressSection = () => {
    if (!loading && trainingProgress === 0) return null;

    return (
      <Box sx={{ mt: 3, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          {i18n.t("assistants.training.progress")}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={trainingProgress}
          sx={{ height: 8, borderRadius: 4, mb: 1 }}
        />
        <Typography variant="body2" color="text.secondary">
          {trainingProgress}% {i18n.t("assistants.training.complete")}
        </Typography>
      </Box>
    );
  };

  const renderResultsSection = () => {
    if (!trainingResult) return null;

    return (
      <Box sx={{ mt: 3 }}>
        <Alert severity="success" sx={{ mb: 2 }}>
          <AlertTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <CheckCircleIcon fontSize="small" />
              {i18n.t("assistants.training.success")}
            </Box>
          </AlertTitle>
          {i18n.t("assistants.training.successMessage")}
        </Alert>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
              <AnalyticsIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="primary" fontWeight="bold">
                {trainingResult.ticketsProcessed}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {i18n.t("assistants.training.ticketsProcessed")}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
              <StorageIcon color="secondary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="secondary" fontWeight="bold">
                {trainingResult.trainingData.knowledgeBaseItems}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {i18n.t("assistants.training.knowledgeItems")}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
              <PsychologyIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="success.main" fontWeight="bold">
                {trainingResult.trainingData.statistics.satisfactionRate || 'N/A'}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {i18n.t("assistants.training.satisfaction")}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {trainingResult.trainingData.companyProfile && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {i18n.t("assistants.training.companyProfile")}:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>{i18n.t("assistants.training.sector")}:</strong> {trainingResult.trainingData.companyProfile.sector || 'N/A'}
            </Typography>
            {trainingResult.trainingData.companyProfile.commonTopics && trainingResult.trainingData.companyProfile.commonTopics.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>{i18n.t("assistants.training.commonTopics")}:</strong>
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {trainingResult.trainingData.companyProfile.commonTopics.slice(0, 5).map((topic, index) => (
                    <Chip key={index} label={topic} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: '60vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <PsychologyIcon color="primary" />
            <Box>
              <Typography variant="h6">
                {i18n.t("assistants.training.title")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {assistant?.name}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent>
          {!trainingResult ? (
            <Box>
              <Typography variant="body1" color="text.secondary" paragraph>
                {i18n.t("assistants.training.description")}
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={i18n.t("assistants.training.maxTickets")}
                    type="number"
                    value={config.maxTickets}
                    onChange={(e) => setConfig({...config, maxTickets: parseInt(e.target.value) || 500})}
                    inputProps={{ min: 1, max: 2000 }}
                    helperText={i18n.t("assistants.training.maxTicketsHelp")}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={i18n.t("assistants.training.minMessages")}
                    type="number"
                    value={config.minMessagesPerTicket}
                    onChange={(e) => setConfig({...config, minMessagesPerTicket: parseInt(e.target.value) || 3})}
                    inputProps={{ min: 1, max: 50 }}
                    helperText={i18n.t("assistants.training.minMessagesHelp")}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <DatePicker
                    label={i18n.t("assistants.training.startDate")}
                    value={config.dateRange.start}
                    onChange={(date) => setConfig({
                      ...config,
                      dateRange: {...config.dateRange, start: date}
                    })}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                    maxDate={config.dateRange.end}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <DatePicker
                    label={i18n.t("assistants.training.endDate")}
                    value={config.dateRange.end}
                    onChange={(date) => setConfig({
                      ...config,
                      dateRange: {...config.dateRange, end: date}
                    })}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                    minDate={config.dateRange.start}
                    maxDate={new Date()}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={queues}
                    getOptionLabel={(option) => option.name}
                    value={queues.filter(queue => config.queueIds.includes(queue.id))}
                    onChange={(event, newValue) => {
                      setConfig({
                        ...config,
                        queueIds: newValue.map(queue => queue.id)
                      });
                    }}
                    loading={loadingQueues}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option.name}
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={i18n.t("assistants.training.queues")}
                        placeholder={i18n.t("assistants.training.queuesPlaceholder")}
                        helperText={i18n.t("assistants.training.queuesHelp")}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.includeClosedTickets}
                        onChange={(e) => setConfig({...config, includeClosedTickets: e.target.checked})}
                      />
                    }
                    label={i18n.t("assistants.training.includeClosed")}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.onlySuccessfulTickets}
                        onChange={(e) => setConfig({...config, onlySuccessfulTickets: e.target.checked})}
                      />
                    }
                    label={i18n.t("assistants.training.onlySuccessful")}
                  />
                </Grid>
              </Grid>

              {renderProgressSection()}
            </Box>
          ) : (
            renderResultsSection()
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            {trainingResult ? i18n.t("assistants.training.close") : i18n.t("assistants.training.cancel")}
          </Button>
          {!trainingResult && (
            <Button
              onClick={handleStartTraining}
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <PsychologyIcon />}
            >
              {loading ? i18n.t("assistants.training.training") : i18n.t("assistants.training.start")}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default TrainingModal;